import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  useToast,
  ModalBody,
  ModalCloseButton,
  Select,
  FormLabel,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdDone, MdCancel, MdAdd, MdRemove } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const LaundryCategories = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [error, setError] = useState("");
  const [editableCategory, setEditableCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInsertForm, setShowInsertForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    translation_key: "",
    translations: { fr: "", it: "" }
  });
  const toast = useToast();
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });
  const pageId= 13; 

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === 13);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/laundry/categories?page_id=${pageId}`,
          "GET"
        );
        setCategories(response.data);
        setFilteredCategories(response.data);
      } catch (error) {
        toast({
          title: "Failed to fetch categories.",
          status: "error",
          duration: 2500,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchCategories();
  }, [toast, pageId]);

  const handleDeleteCategory = async (categoryId) => {
    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/laundry/categories/category/delete?id=${categoryId}&page_id=${pageId}`,
        "DELETE"
      );
      setCategories(categories.filter((category) => category.id !== categoryId));
      setFilteredCategories(filteredCategories.filter((category) => category.id !== categoryId));
      toast({
        title: "Category deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      setError("Failed to delete category");
    }
  };

  const handleEditCategory = (category) => {
    setEditableCategory({ ...category });
  };

  const handleSaveCategory = async () => {
    try {
      const data = {
        id: editableCategory.id,
        name: editableCategory.name,
        translation_key: editableCategory.translation_key,
        translations: editableCategory.translations,
      };

      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/laundry/categories/category?page_id=${pageId}`,
        "PATCH",
        data
      );

      setCategories((prevCategories) =>
        prevCategories.map((category) => {
          if (category.id === editableCategory.id) {
            return { ...category, ...editableCategory };
          }
          return category;
        })
      );

      setFilteredCategories((prevFilteredCategories) =>
        prevFilteredCategories.map((category) => {
          if (category.id === editableCategory.id) {
            return { ...category, ...editableCategory };
          }
          return category;
        })
      );

      setEditableCategory(null);
      
      toast({
        title: "Category updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Error updating category:", error);
      setError(error.message);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/laundry/categories?page_id=${pageId}`,
        "POST",
        newCategory
      );

      toast({
        title: "Category created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Reset form
      setNewCategory({
        name: "",
        translation_key: "",
        translations: { fr: "", it: "" }
      });
      setShowInsertForm(false);

      // Refresh categories list
      const updatedResponse = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/laundry/categories?page_id=${pageId}`,
        "GET"
      );
      setCategories(updatedResponse.data);
      setFilteredCategories(updatedResponse.data);
    } catch (error) {
      console.error("Error creating category:", error);
      toast({
        title: "Failed to create category",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = categories.filter((category) =>
      category.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCategories(filtered);
  };

  return (
    <>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ base: "scroll", lg: "hidden" }}
        boxShadow="lg"
      >
        <Flex
          px={{ base: "10px", md: "25px" }}
          justify="space-between"
          mb="20px"
          align="center"
          flexWrap="wrap"
        >
          <Flex align="center">
            <Text
              color="black"
              fontSize={{ base: "18px", md: "22px" }}
              fontWeight="700"
              lineHeight="100%"
            >
              {t("Laundry Categories")}
            </Text>
            <Button
              onClick={() => setShowInsertForm(!showInsertForm)}
              isDisabled={!permissions.canInsert}
              ml={4}
              leftIcon={showInsertForm ? <MdRemove /> : <MdAdd />}
              colorScheme={showInsertForm ? "red" : "cyan"}
              color="white"
              p={4}
              borderRadius="full"
              boxShadow="md"
              _hover={{
                bg: showInsertForm ? "red.600" : "cyan.600",
                transform: "scale(1.05)",
              }}
              _active={{
                bg: showInsertForm ? "red.700" : "cyan.700",
                transform: "scale(0.95)",
              }}
              transition="all 0.2s"
            >
              {showInsertForm ? t("Cancel") : t("Add Category")}
            </Button>
          </Flex>
          {error && <Text color="red.500">{error}</Text>}
          <Input
            placeholder={t("Search categories")}
            value={searchQuery}
            onChange={handleSearch}
            size="sm"
            w={{ base: "100%", md: "200px" }}
            mt={{ base: 2, md: 0 }}
          />
        </Flex>

        {/* Insert Category Modal */}
        <Modal
          isOpen={showInsertForm}
          onClose={() => setShowInsertForm(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("Create Laundry Category")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb={4}>
                <FormLabel color="blue.600">
                  {t("Category Name")}
                </FormLabel>
                <Input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      name: e.target.value,
                    })
                  }
                  placeholder={t("Enter category name")}
                />
              </Box>
              <Box mb={4}>
                <FormLabel color="blue.600">
                  {t("Translation Key")}
                </FormLabel>
                <Input
                  value={newCategory.translation_key}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      translation_key: e.target.value,
                    })
                  }
                  placeholder={t("Enter translation key")}
                />
              </Box>
              <Box mb={4}>
                <FormLabel color="blue.600">
                  {t("Translation ")}
                  <Text as="span" fontWeight={"bold"}>({t("French")})</Text>
                </FormLabel>
                <Input
                  value={newCategory.translations.fr}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      translations: {
                        ...newCategory.translations,
                        fr: e.target.value,
                      },
                    })
                  }
                  placeholder={t("Enter French translation")}
                />
              </Box>
              <Box mb={4}>
                <FormLabel color="blue.600">
                  {t("Translation ")}
                  <Text as="span" fontWeight={"bold"}>({t("Italy")})</Text>
                </FormLabel>
                <Input
                  value={newCategory.translations.it}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      translations: {
                        ...newCategory.translations,
                        it: e.target.value,
                      },
                    })
                  }
                  placeholder={t("Enter Italian translation")}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="cyan"
                color="white"
                mr={3}
                onClick={handleCreateCategory}
              >
                {t("Create")}
              </Button>
              <Button onClick={() => setShowInsertForm(false)}>
                {t("Cancel")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Box overflowY="auto" maxHeight="400px">
         <Table variant="striped" colorScheme="gray" mb="24px" size="sm">
  <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
               <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
      <Th color="white">{t("Index")}</Th>
      <Th color="white">{t("Name")}</Th>
      <Th color="white">{t("Translations")}</Th>
      <Th color="white">{t("Actions")}</Th>
    </Tr>
  </Thead>

  <Tbody>
    {filteredCategories.map((category, index) => (
      <Tr
        key={category.id}
        bg={editableCategory?.id === category.id ? "blue.50" : "transparent"}
        borderWidth={editableCategory?.id === category.id ? "2px" : "0"}
        borderColor={editableCategory?.id === category.id ? "blue.400" : "transparent"}
        _hover={{
          bg: editableCategory?.id === category.id ? "blue.100" : "gray.50",
        }}
      >
        {/* Index */}
        <Td>{index + 1}</Td>

        {/* Name */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Input
              value={editableCategory.name || ""}
              onChange={(e) =>
                setEditableCategory({ ...editableCategory, name: e.target.value })
              }
              fontSize={{ base: "sm", md: "md" }}
              focusBorderColor="blue.400"
              borderColor="blue.300"
            />
          ) : (
            <Text fontSize={{ base: "sm", md: "md" }}>{category.name}</Text>
          )}
        </Td>

        {/* Translations */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Flex direction="column" gap={2}>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>Key:</Text>
                <Input
                  value={editableCategory.translation_key || ""}
                  size="sm"
                  isDisabled
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>FR:</Text>
                <Input
                  value={editableCategory.translations?.fr || ""}
                  onChange={(e) =>
                    setEditableCategory({
                      ...editableCategory,
                      translations: { ...editableCategory.translations, fr: e.target.value },
                    })
                  }
                  size="sm"
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>IT:</Text>
                <Input
                  value={editableCategory.translations?.it || ""}
                  onChange={(e) =>
                    setEditableCategory({
                      ...editableCategory,
                      translations: { ...editableCategory.translations, it: e.target.value },
                    })
                  }
                  size="sm"
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column">
              {category.translation_key && (
                <Text fontSize="sm" fontWeight="medium">Key: {category.translation_key}</Text>
              )}
              {category.translations?.fr && (
                <Text fontSize="sm">FR: {category.translations.fr}</Text>
              )}
              {category.translations?.it && (
                <Text fontSize="sm">IT: {category.translations.it}</Text>
              )}
              {!category.translation_key &&
                !category.translations?.fr &&
                !category.translations?.it && (
                  <Text fontSize="sm" color="gray.500">{t("No translations")}</Text>
                )}
            </Flex>
          )}
        </Td>

        {/* Actions */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Flex gap={2}>
              <Button
                colorScheme="green"
                size="sm"
                onClick={handleSaveCategory}
                borderRadius="full"
                leftIcon={<MdDone />}
              >
                {t("Save")}
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={() => setEditableCategory(null)}
                borderRadius="full"
                leftIcon={<MdCancel />}
              >
                {t("Cancel")}
              </Button>
            </Flex>
          ) : (
            <Flex gap={2}>
              <Button
                colorScheme="cyan"
                size="sm"
                color="white"
                onClick={() => handleEditCategory(category)}
                borderRadius="full"
                leftIcon={<MdEdit />}
                _hover={{ bg: "cyan.600" }}
              >
                {t("Edit")}
              </Button>
              {/* <Button
                colorScheme="red"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(t("Are you sure you want to delete this category?"))
                  ) {
                    handleDeleteCategory(category.id);
                  }
                }}
                borderRadius="full"
                leftIcon={<MdDelete />}
                _hover={{ bg: "red.600" }}
              >
                {t("Delete")}
              </Button> */}
            </Flex>
          )}
        </Td>
      </Tr>
    ))}
  </Tbody>
</Table>

        </Box>
      </Card>
    </>
  );
};

export default LaundryCategories;
